/* eslint-disable max-lines */
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Box, Checkbox, Collapse, FormControlLabel, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { WheelTimePicker } from "@src/appV2/redesign/components/WheelTimePicker/WheelTimePicker";
import {
  CbhIcon,
  Divider,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import {
  addHours,
  addMilliseconds,
  addMinutes,
  differenceInMilliseconds,
  isAfter,
  isBefore,
} from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { type Duration, DurationInput } from "../../../DateTime/DurationInput";
import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import {
  type BreakErrorType,
  validateBreakTimestamps,
} from "../../MissedPunches/utils/validateBreakTimestamps";
import { isBoundaryError } from "../analytics/isBoundaryError";
import {
  getBottomSheetIntent,
  TimekeepingAdjustmentBottomSheet,
} from "../TimekeepingAdjustmentBottomSheet";
import { inferDateFromTime } from "./utils/inferDateFromTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditBreakTimeFormValues {
  breakStart?: Date;
  breakDuration: Duration;
}

interface EditBreakTimeBottomSheetProps {
  modalState: UseModalState;
  breakStartTime?: Date;
  defaultBreakDuration: Duration;
  breakDuration?: Duration;
  initialBreakSkipped?: boolean;
  workplaceTimezone: string;
  shiftStartAt: Date;
  shiftEndAt: Date;
  clockInTime: Date;
  clockOutTime: Date;
  verifiedBreakStartTime?: Date;
  verifiedBreakEndTime?: Date;
  isMissedPunchRequestEnabled: boolean;
  isMissedPunchRequestEnabledV2?: boolean;
  isShiftVerified: boolean;
  shiftId?: string;
  workerId?: string;
  workplaceId?: string;
  timesheetFlow?: string;
  workplaceName?: string;
  breakPolicyText?: string;
  mandatoryBreakDurationMinutes?: number;
  onConfirm: (breakStartTime: Date | undefined, breakDuration: Duration, reason: string) => void;
  onCancel?: () => void;
}

type ActiveSelector = "breakStart" | "breakDuration" | undefined;

function formatBreakDuration(duration: Duration, isSkipped: boolean): string {
  if (isSkipped) {
    return "Skipped";
  }

  if (duration.hours > 0) {
    const minutesFormatted = duration.minutes.toString().padStart(2, "0");
    return `${duration.hours} hr ${minutesFormatted} min`;
  }

  return `${duration.minutes} min`;
}

function calculateMidpointOfTimes(clockInTime: Date, clockOutTime: Date): Date {
  const durationInMilliseconds = differenceInMilliseconds(clockOutTime, clockInTime);
  return addMilliseconds(clockInTime, Math.trunc(durationInMilliseconds / 2));
}

function formatBreakStartDisplay(
  breakSkipped: boolean,
  currentLocalBreakStartTime?: Date,
  workplaceTimezone?: string
): string {
  if (breakSkipped) {
    return "--:-- --";
  }

  if (isDefined(currentLocalBreakStartTime) && isDefined(workplaceTimezone)) {
    const utcTime = getUtcTimeFromPicker(currentLocalBreakStartTime, workplaceTimezone);
    return formatTimeWithTimezone(utcTime, workplaceTimezone, false);
  }

  return "--:-- --";
}

export function EditBreakTimeBottomSheet(props: EditBreakTimeBottomSheetProps) {
  const {
    modalState,
    breakStartTime,
    defaultBreakDuration,
    breakDuration = defaultBreakDuration,
    initialBreakSkipped = false,
    workplaceTimezone,
    clockInTime,
    clockOutTime,
    verifiedBreakStartTime,
    verifiedBreakEndTime,
    isMissedPunchRequestEnabled,
    isMissedPunchRequestEnabledV2 = false,
    shiftStartAt,
    shiftEndAt,
    isShiftVerified,
    shiftId,
    workerId,
    workplaceId,
    timesheetFlow,
    workplaceName,
    breakPolicyText,
    mandatoryBreakDurationMinutes,
    onConfirm,
    onCancel,
  } = props;

  const [activeSelector, setActiveSelector] = useState<ActiveSelector>();
  const [localBreakSkipped, setLocalBreakSkipped] = useState(initialBreakSkipped);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [isPickerScrolling, setIsPickerScrolling] = useState(false);
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);

  const getDefaultBreakStartTime = useCallback(() => {
    if (breakStartTime) {
      return getLocalTimeForPicker(breakStartTime, workplaceTimezone);
    }

    if (initialBreakSkipped) {
      const acknowledgmentTime = calculateMidpointOfTimes(clockInTime, clockOutTime);
      return getLocalTimeForPicker(acknowledgmentTime, workplaceTimezone);
    }

    return undefined;
  }, [breakStartTime, initialBreakSkipped, clockInTime, clockOutTime, workplaceTimezone]);

  const { setValue, watch, reset, formState } = useForm<EditBreakTimeFormValues>({
    defaultValues: {
      breakStart: getDefaultBreakStartTime(),
      breakDuration,
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      reset({
        breakStart: getDefaultBreakStartTime(),
        breakDuration,
      });
      setLocalBreakSkipped(initialBreakSkipped);
      setSaveAttempted(false);
      setActiveSelector(undefined);
      setPolicyAcknowledged(false);
    }
  }, [modalState.modalIsOpen, reset, getDefaultBreakStartTime, breakDuration, initialBreakSkipped]);

  const currentLocalBreakStartTime = watch("breakStart");
  const currentBreakDuration = watch("breakDuration");

  const currentBreakStartTime = currentLocalBreakStartTime
    ? getUtcTimeFromPicker(currentLocalBreakStartTime, workplaceTimezone)
    : undefined;

  const calculateBreakEndTime = (startTime?: Date, duration?: Duration): Date | undefined => {
    if (!isDefined(startTime) || !isDefined(duration) || localBreakSkipped) {
      return undefined;
    }

    let endTime = new Date(startTime);
    if (duration.hours > 0) {
      endTime = addHours(endTime, duration.hours);
    }

    if (duration.minutes > 0) {
      endTime = addMinutes(endTime, duration.minutes);
    }

    return endTime;
  };

  const getValidationResult = (
    startTime?: Date,
    duration?: Duration
  ): { errorMessage?: string; errorType?: BreakErrorType } => {
    const endTime = calculateBreakEndTime(startTime, duration);

    if (isMissedPunchRequestEnabled) {
      const validationResult = validateBreakTimestamps({
        breakStartTime: startTime,
        breakEndTime: endTime,
        clockInTime,
        clockOutTime,
        isShiftVerified,
        isBreakSkipped: localBreakSkipped,
        verifiedBreakStartTime,
        verifiedBreakEndTime,
      });

      if (!validationResult.isValid) {
        return {
          errorMessage: validationResult.message,
          errorType: validationResult.errorType,
        };
      }

      return {};
    }

    if (localBreakSkipped || isNil(startTime)) {
      return {};
    }

    if (isBefore(startTime, clockInTime)) {
      return { errorMessage: "You cannot start your break before your clock in time." };
    }

    if (isAfter(startTime, clockOutTime)) {
      return { errorMessage: "You cannot start your break after your clock out time." };
    }

    if (isDefined(endTime) && isAfter(endTime, clockOutTime)) {
      return { errorMessage: "You cannot end your break after your clock out time." };
    }

    return {};
  };

  // Only show error intent after save was attempted
  const { errorMessage } = saveAttempted
    ? getValidationResult(currentBreakStartTime, currentBreakDuration)
    : {};

  // Called by the shell when the user taps Save on the picker step.
  // Returns false to block step advancement when there is a validation error.
  const handlePickerConfirm = (): boolean => {
    setSaveAttempted(true);

    // For skipped breaks, skip further validation
    if (localBreakSkipped) {
      return true;
    }

    const currentValidation = getValidationResult(currentBreakStartTime, currentBreakDuration);

    if (isBoundaryError(currentValidation.errorType)) {
      logEvent(APP_V2_USER_EVENTS.MISSED_PUNCH_REQUEST_TIMESHEET_BOUNDARY_VIOLATION_ATTEMPTED, {
        actor: "worker",
        clockAction: "break",
        errorType: currentValidation.errorType,
        shiftId,
        workplaceId,
        selectedTime: currentBreakStartTime?.toISOString(),
        clockInTime: clockInTime.toISOString(),
        clockOutTime: clockOutTime.toISOString(),
        verifiedBreakStartTime: verifiedBreakStartTime?.toISOString(),
        verifiedBreakEndTime: verifiedBreakEndTime?.toISOString(),
      });
    }

    if (currentValidation.errorMessage) {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_FORM_VALIDATION_ERROR, {
        shiftId,
        workerId,
        workplaceId,
        error: currentValidation.errorMessage,
        errorType: currentValidation.errorType,
        timekeepingAction: "break",
        timesheetFlow,
      });
      return false;
    }

    if (!isDefined(currentBreakStartTime)) {
      return false;
    }

    return true;
  };

  const handleTimeChange = useCallback(
    (newTime: Date | undefined) => {
      if (isDefined(newTime)) {
        const inferResult = inferDateFromTime({
          timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
          shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
          shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
          earlyBufferMinutes: 0,
        });

        const timeToSet = inferResult.success ? inferResult.date : newTime;
        setValue("breakStart", timeToSet, { shouldDirty: true });

        if (localBreakSkipped) {
          setValue("breakDuration", defaultBreakDuration, { shouldDirty: true });
          setLocalBreakSkipped(false);
        }

        setSaveAttempted(false);
      }
    },
    [localBreakSkipped, defaultBreakDuration, setValue, shiftStartAt, shiftEndAt, workplaceTimezone]
  );

  const handleDurationChange = (newDuration: Duration) => {
    setValue("breakDuration", newDuration, { shouldDirty: true });

    const isNewDurationZero = newDuration.hours === 0 && newDuration.minutes === 0;

    if (localBreakSkipped && !isNewDurationZero) {
      const acknowledgmentTime = calculateMidpointOfTimes(clockInTime, clockOutTime);
      const localAcknowledgmentTime = getLocalTimeForPicker(acknowledgmentTime, workplaceTimezone);
      setValue("breakStart", localAcknowledgmentTime, { shouldDirty: true });
      setLocalBreakSkipped(false);
    } else if (!localBreakSkipped && isNewDurationZero) {
      setValue("breakStart", undefined, { shouldDirty: true });
      setLocalBreakSkipped(true);
    }

    setSaveAttempted(false);
  };

  const toggleSelector = (selector: ActiveSelector) => {
    setActiveSelector(activeSelector === selector ? undefined : selector);
  };

  useEffect(() => {
    if (activeSelector === "breakStart" && localBreakSkipped) {
      handleTimeChange(getDefaultBreakStartTime());
    }
  }, [activeSelector, localBreakSkipped, handleTimeChange, getDefaultBreakStartTime]);

  const isPickerValid = localBreakSkipped || isDefined(currentBreakStartTime);
  const hasChanges = formState.isDirty || localBreakSkipped !== initialBreakSkipped;

  const defaultTotalMinutes = defaultBreakDuration.hours * 60 + defaultBreakDuration.minutes;
  const currentTotalMinutes = currentBreakDuration.hours * 60 + currentBreakDuration.minutes;
  const hasPolicyContent =
    isDefined(breakPolicyText) &&
    isDefined(workplaceName) &&
    isDefined(mandatoryBreakDurationMinutes);
  const isDecreasingBreak = localBreakSkipped || currentTotalMinutes < defaultTotalMinutes;
  const requiresApproval = isDecreasingBreak || (hasPolicyContent && hasChanges);
  const showApprovalIntent =
    hasChanges &&
    isDecreasingBreak &&
    !isPickerScrolling &&
    !errorMessage &&
    isMissedPunchRequestEnabledV2;

  let activeIntentType: "error" | "info" | undefined;
  if (errorMessage) {
    activeIntentType = "error";
  } else if (showApprovalIntent) {
    activeIntentType = "info";
  }
  const { intent, intentIcon } = isDefined(activeIntentType)
    ? getBottomSheetIntent(activeIntentType)
    : { intent: undefined, intentIcon: undefined };

  const intentContent =
    errorMessage ??
    (showApprovalIntent
      ? "You are requesting a shorter break. No worries - you'll be able to add the reason on the next screen."
      : undefined);

  const acknowledgementStepContent = hasPolicyContent ? (
    <>
      <Title semibold variant="h4" component="h2">
        Break policy
      </Title>
      <Text variant="body2">
        {workplaceName} requires that you take a {mandatoryBreakDurationMinutes}-minute unpaid
        break. As a reminder, here is their mandatory break policy:
      </Text>
      <Text variant="body2" sx={{ fontStyle: "italic" }}>
        {breakPolicyText}
      </Text>
      <FormControlLabel
        control={
          <Checkbox
            checked={policyAcknowledged}
            onChange={(event) => setPolicyAcknowledged(event.target.checked)}
          />
        }
        label={
          <Text variant="body2">
            I acknowledge {workplaceName}&apos;s break policy, and my adjusted break is consistent
            with this policy
          </Text>
        }
      />
    </>
  ) : undefined;

  return (
    <TimekeepingAdjustmentBottomSheet
      modalState={modalState}
      intent={intent}
      intentContent={intentContent}
      intentIcon={intentIcon}
      pickerTitle="Adjust break"
      hasChanges={hasChanges}
      isPickerScrolling={isPickerScrolling}
      isPickerValid={isPickerValid}
      requiresApproval={requiresApproval}
      acknowledgementStepContent={acknowledgementStepContent}
      isAcknowledgementStepComplete={policyAcknowledged}
      reasonTitle="Why did you adjust your break?"
      onPickerConfirm={handlePickerConfirm}
      onConfirm={(reason) => {
        if (localBreakSkipped) {
          onConfirm(undefined, { hours: 0, minutes: 0 }, reason);
        } else if (isDefined(currentBreakStartTime)) {
          onConfirm(currentBreakStartTime, currentBreakDuration, reason);
        }
      }}
      onCancel={onCancel}
    >
      <Stack spacing={4}>
        <Stack direction="row" spacing={3} alignItems="center">
          <CbhIcon size="medium" type="break" />
          <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
            Break start
          </Text>
          <Box textAlign="right">
            <Button
              aria-label="Select Break start"
              variant="outlined"
              size="small"
              endIconType="pencil"
              isSelected={activeSelector === "breakStart"}
              onClick={() => {
                toggleSelector("breakStart");
              }}
            >
              {formatBreakStartDisplay(
                localBreakSkipped,
                currentLocalBreakStartTime,
                workplaceTimezone
              )}
            </Button>
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} alignItems="center">
          <CbhIcon size="medium" type="clock" />
          <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
            Break duration
          </Text>
          <Box textAlign="right">
            <Button
              aria-label="Select Break duration"
              variant="outlined"
              size="small"
              endIconType="pencil"
              isSelected={activeSelector === "breakDuration"}
              onClick={() => {
                toggleSelector("breakDuration");
              }}
            >
              {formatBreakDuration(currentBreakDuration, localBreakSkipped)}
            </Button>
          </Box>
        </Stack>

        <Collapse in={activeSelector === "breakStart"} timeout={300}>
          <Stack spacing={2}>
            <Divider />
            <WheelTimePicker
              preventParentTouchStart
              value={currentLocalBreakStartTime}
              containerSx={{
                alignSelf: "center",
              }}
              onChange={handleTimeChange}
              onScrollingChange={setIsPickerScrolling}
            />
          </Stack>
        </Collapse>

        <Collapse in={activeSelector === "breakDuration"} timeout={300}>
          <DurationInput
            preventParentTouchStart
            value={currentBreakDuration}
            maxHours={4}
            minutesStep={5}
            onChange={handleDurationChange}
          />
        </Collapse>

        <Box textAlign="center">
          <Button
            variant={localBreakSkipped ? "contained" : "outlined"}
            size="small"
            onClick={() => {
              setLocalBreakSkipped(true);
              setValue("breakDuration", { hours: 0, minutes: 0 });
              setValue("breakStart", undefined);
              setSaveAttempted(false);
            }}
          >
            Skipped break
          </Button>
        </Box>
      </Stack>
    </TimekeepingAdjustmentBottomSheet>
  );
}
/* eslint-enable max-lines */
